import { normalizePhone, type Me } from "@elektr/shared";
import { Zap } from "lucide-react";
import { useState, type FormEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";

import { ThemeToggle } from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Field, Input } from "@/components/ui/form";
import { HeroArt } from "@/components/ui/hero";
import { api, errorMessage, unwrap } from "@/lib/api";
import { homeFor, useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";
import { cn } from "@/lib/utils";

type Mode = "password" | "otp";

export function LoginPage() {
  const { t, lang, setLang } = useT();
  const { user, login, completeLogin } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const state = loc.state as { from?: string } | null;

  // Kod bilan kirish faqat SMS yoqilgan bo'lsa ko'rinadi (aks holda tugma ishlamasdi)
  const cfg = useQuery({
    queryKey: ["public-config"],
    queryFn: async () => unwrap(await api.GET("/api/public-config/")),
    staleTime: 60_000,
    retry: false,
  });
  const otpEnabled = cfg.data?.otp_enabled === true;
  const [mode, setMode] = useState<Mode>("password");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to={homeFor(user.role, state?.from)} replace />;

  const normalized = normalizePhone(phone);

  async function run(fn: () => Promise<void>) {
    setError(null);
    if (!normalized) {
      setError(t("auth.phone_invalid"));
      return;
    }
    setBusy(true);
    try {
      await fn();
    } catch (e) {
      setError(errorMessage(e, t("common.error")));
    } finally {
      setBusy(false);
    }
  }

  const enter = (me: Me | null) => {
    if (me) nav(homeFor(me.role, state?.from), { replace: true });
  };

  const submitPassword = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      const me = await login(normalized!, password);
      if (!me) setError(t("auth.invalid_credentials"));
      else enter(me);
    });
  };

  const requestCode = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      unwrap(await api.POST("/api/auth/otp/request/", { body: { phone: normalized! } }));
      setCodeSent(true);
    });
  };

  const verifyCode = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      const res = await api.POST("/api/auth/otp/verify/", { body: { phone: normalized!, code } });
      if (res.response.status === 400) {
        setError(t("auth.code_invalid"));
        return;
      }
      const data = unwrap(res);
      enter(await completeLogin(data.access, data.refresh));
    });
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-[1.1fr_1fr]">
      <div className="hero-gradient relative hidden overflow-hidden p-10 text-white lg:flex lg:flex-col lg:justify-between">
        <div className="anim-drift pointer-events-none absolute -right-24 -top-24 size-96 rounded-full bg-white/15 blur-3xl" />
        <div
          className="anim-drift pointer-events-none absolute -bottom-32 -left-16 size-96 rounded-full bg-fuchsia-400/25 blur-3xl"
          style={{ animationDelay: "-5s" }}
        />
        <div className="relative flex items-center gap-2.5 text-xl font-extrabold">
          <span className="flex size-10 items-center justify-center rounded-xl bg-white/20 ring-1 ring-white/30">
            <Zap className="size-5 fill-current" />
          </span>
          Elektr Pasport
        </div>
        <div className="relative mx-auto w-full max-w-md">
          <HeroArt />
        </div>
        <p className="relative max-w-md text-2xl font-bold leading-snug">{t("auth.tagline")}</p>
      </div>
      <div className="flex items-center justify-center p-4">
        <Card className="anim-pop w-full max-w-sm p-6">
          <div className="mb-5 flex items-center justify-between">
            <div className="flex items-center gap-2.5 text-xl font-extrabold">
              <span className="hero-gradient flex size-9 items-center justify-center rounded-xl text-white lg:hidden">
                <Zap className="size-5 fill-current" />
              </span>
              <span className="lg:hidden">Elektr Pasport</span>
              <span className="hidden lg:inline">{t("auth.login")}</span>
            </div>
            <div className="flex items-center gap-1">
              <ThemeToggle />
              <button
                className="text-xs font-bold uppercase text-primary"
                onClick={() => setLang(lang === "uz" ? "ru" : "uz")}
              >
                {lang === "uz" ? "RU" : "UZ"}
              </button>
            </div>
          </div>

          {otpEnabled && (
            <div className="mb-4 grid grid-cols-2 gap-1 rounded-lg bg-muted p-1" role="tablist">
              {(["password", "otp"] as const).map((m) => (
                <button
                  key={m}
                  role="tab"
                  aria-selected={mode === m}
                  onClick={() => {
                    setMode(m);
                    setError(null);
                  }}
                  className={cn(
                    "min-h-10 rounded-md text-sm font-semibold",
                    mode === m ? "bg-card shadow-sm" : "text-muted-foreground",
                  )}
                >
                  {m === "password" ? t("auth.tab_password") : t("auth.tab_code")}
                </button>
              ))}
            </div>
          )}

          <form
            onSubmit={mode === "password" ? submitPassword : codeSent ? verifyCode : requestCode}
            className="flex flex-col gap-4"
            noValidate
          >
            <Field label={t("auth.phone")} hint={t("auth.phone_hint")}>
              {(id) => (
                <Input
                  id={id}
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="90 123 45 67"
                  value={phone}
                  disabled={codeSent && mode === "otp"}
                  onChange={(e) => setPhone(e.target.value)}
                />
              )}
            </Field>

            {mode === "password" && (
              <Field label={t("auth.password")}>
                {(id) => (
                  <Input
                    id={id}
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                )}
              </Field>
            )}
            {mode === "otp" && codeSent && (
              <Field label={t("auth.code")} hint={t("auth.code_hint")}>
                {(id) => (
                  <Input
                    id={id}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  />
                )}
              </Field>
            )}

            {error && (
              <p role="alert" className="text-sm text-danger">
                {error}
              </p>
            )}
            {mode === "password" && (
              <Link
                to="/forgot"
                className="text-center text-sm font-medium text-primary underline-offset-4 hover:underline"
              >
                {t("auth.forgot")}
              </Link>
            )}
            <Button type="submit" disabled={busy}>
              {mode === "password"
                ? t("auth.login")
                : codeSent
                  ? t("auth.verify")
                  : t("auth.send_code")}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}
